/* eslint-disable react-hooks/exhaustive-deps */
import { Locale } from "@common/constans";
import { ContactEmail, ContactNumber, Feedback, IContactEmail, IFeedback, IGResponse } from "@common/models";
import { IContactPoint } from "@common/models/CMS";
import { Dialog, DialogHeader } from "@components/general/dialog";
import Divider from "@components/general/Divider";
import Button from "@components/general/form/Button";
import DropDown from "@components/general/form/DropDown";
import FileInput, { IBase64 } from "@components/general/form/FileInput";
import Form from "@components/general/form/Form";
import PhoneSection from "@components/general/form/PhoneSection";
import TextArea from "@components/general/form/TextArea";
import TextInput from "@components/general/form/TextInput";
import Label from "@components/general/Label";
import { MessageBox, MessageBoxBtn, MessageBoxBtnGroup } from "@components/general/MessageBox";
import Panel from "@components/general/Panel";
import { Culture, IValidationError } from "@core/index";
import { CONTACT_POINT, useApolloQuery } from "@service/graphql";
import Notification from "@service/Notification";
import { Action, participateCreateFeedback, participateDeleteFeedback, participateFeedbacks, participateModifyFeedback } from "@service/proxy";
import Translate from "@service/Translate";
import { formatToBase64 } from "@utilize/Format";
import { nameof, validationMessage } from "@utilize/Util";
import { FC, Fragment, useContext, useEffect, useRef, useState } from "react";
import { CSSTransition } from 'react-transition-group';
import { FeedbackItemsSkeleton } from "./FeedbackItemsSkeleton";
import { ParticipateAction } from "./Action";
import { ParticipateContext } from "./Context";
import { FeedbackConfirmedItems } from "./FeedbackConfirmedItems";

interface ILoadingStatus {
    submitBtnLoading?: boolean,
    addFeedbackBtn?: boolean,
    successfulMessageBoxDialog?: boolean,
    feedbacksSkeleton?: boolean,
    modifyFeedback?: boolean,
    deleteFeedback?: boolean,

}

export const FeedbackItems: FC = () => {
    const { tr } = Translate;
    const IrLocale = Culture.getLocale(Locale.fa);
    const { lang } = Culture.getLocale();
    const feedbackCommentRef = useRef<HTMLDivElement>(null);
    const [validationError, setValidationError] = useState<IValidationError>();
    const { data } = useApolloQuery<IGResponse<IContactPoint[]>>(CONTACT_POINT);
    const { participate, participateDispatch } = useContext(ParticipateContext);
    const [feedback, setFeedback] = useState<IFeedback>({ phoneNumber: participate.phoneNumber, email: participate.email, attachments: [] });
    const [loadingStatus, setLoadingStatus] = useState<ILoadingStatus>({});

    const formErrorsMessages = {
        typeId: {
            required: tr("err_record_required", "case_type")
        },
        categoryId: {
            required: tr("err_record_required", "subject")
        },
        description: {
            required: tr("err_record_required", "comment")
        },
        internationalCode: {
            required: tr("err_record_required", "mobile")
        },
        phoneNumber: {
            required: tr("err_record_required", "mobile"),
            pattern: tr("err_phone_number")
        },
        emailAddress: {
            required: tr("err_record_required", "email"),
            type: tr("err_email")
        }
    };

    useEffect(() => {
        if (feedbackCommentRef && feedbackCommentRef.current)
            feedbackCommentRef.current.classList.add(feedbackCommentRef.current.getBoundingClientRect().height < 38 ? 'hide' : "")
    }, [feedbackCommentRef.current])

    const onFeedbackChange = (name: string, value: any) => {
        if (name === "typeId" || name === "categoryId")
            feedback[name as keyof IFeedback] = value.code;
        else
            feedback[name as keyof IFeedback] = value;
        setFeedback({ ...feedback });
        removeValidation(name, value);
    }

    const removeValidation = (name: string, value: any) => {
        if (validationError)
            delete validationError[name];
        setValidationError({ ...validationError });
    }

    const feedbackdValidation = () => {
        let error = new Feedback(feedback).validate() || {};
        const mobile = new ContactNumber(feedback.phoneNumber || {});
        const email = new ContactEmail(feedback.email || {});
        if (feedback.phoneNumber?.phoneNumber)
            error = Object.assign(error, mobile.validate('internationalCode', 'phoneNumber'));
        if (feedback.email?.emailAddress)
            error = Object.assign(error, email.validate());
        if (!feedback.email?.emailAddress && !feedback.phoneNumber?.phoneNumber) {
            error = Object.assign(error, mobile.validate('internationalCode', 'phoneNumber'));
            error = Object.assign(error, email.validate());
        }
        return error;
    }

    const onSubmitBtnClick = () => {
        const error = feedbackdValidation();
        if (Object.keys(error).length)
            return setValidationError({ ...error });
        setValidationError({});
        setLoadingStatus((previousState) => ({ ...previousState, submitBtnLoading: true }));
        participateCreateFeedback(Action.POST, { data: { ...feedback, id: participate.id } })
            .then((response) => {
                setLoadingStatus((previousState) => ({ ...previousState, addFeedbackBtn: true, successfulMessageBoxDialog: true }));
                feedback.referenceId = response.content;
                setFeedback({ ...feedback });
            })
            .catch((error) => { })
            .finally(() => {
                setLoadingStatus((previousState) => ({ ...previousState, submitBtnLoading: false }));
            })
    }

    const onModifyBtnClick = () => {
        setLoadingStatus((previousState) => ({ ...previousState, submitBtnLoading: true }));
        participateModifyFeedback(Action.POST, { data: { ...feedback, id: participate.id } })
            .then((response) => {
                setLoadingStatus((previousState) => ({ ...previousState, addFeedbackBtn: true, modifyFeedback: false }));
                onUpdateParticipateFeedbacks();
                Notification.success(tr("feedback_modify_msg"))
            })
            .catch((error) => { })
            .finally(() => {
                setLoadingStatus((previousState) => ({ ...previousState, submitBtnLoading: false }));
            })
    }

    const onModifyFeedback = (feedback: IFeedback) => {
        setLoadingStatus((previousState) => ({ ...previousState, addFeedbackBtn: false, modifyFeedback: true }));
        setValidationError({});
        const feedBackSharing = document.getElementById("feedBackSharing");
        window.scrollTo({ top: feedBackSharing?.getBoundingClientRect().height || 150, behavior: "smooth" });
        setFeedback({ ...feedback });
    }

    const onRemoveFeedback = (feedback: IFeedback) => {
        setLoadingStatus((previousState) => ({ ...previousState, deleteFeedback: true }));
    }

    const onRemoveBtnAction = (btn: MessageBoxBtn): Promise<boolean> | void => {
        if (btn === MessageBoxBtn.No)
            setLoadingStatus((previousState) => ({ ...previousState, deleteFeedback: false }));
        else
            return new Promise((resolve) => {
                participateDeleteFeedback(Action.POST, { data: { ...feedback, id: participate.id } })
                    .then((response) => {
                        onUpdateParticipateFeedbacks();
                        setLoadingStatus((previousState) => ({ ...previousState, deleteFeedback: false }));
                        resolve(true);
                    })
                    .catch((error) => { })
                    .finally(() => { })
            })
    }

    const onUpdateParticipateFeedbacks = () => {
        participateFeedbacks(Action.POST, { data: { id: participate.id } })
            .then((response) => {
                participateDispatch({ type: ParticipateAction.Participate, payload: { feedbacks: response.content as IFeedback[] } });
            })
            .catch((error) => { })
    }

    const onFileChange = (name: string, value: File[]) => {
        value.forEach((v) => {
            formatToBase64(v)
                .then((res) => {
                    const attachment: IBase64 = {
                        content: res as string,
                        fileName: v.name,
                        fileSize: v.size,
                        fileType: v.type,
                        creationTime: v.lastModified
                    }
                    feedback.attachments?.push(attachment);
                    setFeedback({ ...feedback });
                })
        });
    }

    const onMobileChange = (name: string, value: any) => {
        feedback.phoneNumber = { ...feedback.phoneNumber, [name]: value };
        removeValidation(name, value);
        setFeedback({ ...feedback });
    }

    const error = validationError ? validationMessage(formErrorsMessages, validationError) : {};

    return (
        <div className="feedback-items">
            <Panel>
                <header className="feedback-items-header">{tr(loadingStatus.modifyFeedback ? "feedback_modify" : "flight_survey_feedback_title")}</header>
                <main>
                    <CSSTransition
                        classNames="fade"
                        in={loadingStatus.addFeedbackBtn}
                        timeout={{ enter: 500, exit: 10 }}
                        unmountOnExit
                    >
                        <div className="feedback-items-add-comment">
                            <Button
                                variant="outlined"
                                color="primary"
                                icon="icon-plus"
                                text={tr("record_add", "message")}
                                onClick={() => { setFeedback({ attachments: [] }); setLoadingStatus((previousState) => ({ ...previousState, addFeedbackBtn: false })) }}
                            />
                        </div>
                    </CSSTransition>
                    <CSSTransition
                        classNames="fade"
                        in={!loadingStatus.addFeedbackBtn}
                        timeout={{ enter: 500, exit: 0 }}
                        unmountOnExit
                    >
                        <Form
                            preventDefault
                            className="feedback-items-form" onSubmit={loadingStatus.submitBtnLoading ? undefined : onSubmitBtnClick}
                        >
                            <div className="row flex spa-2">
                                <div className="col-1-2">
                                    <DropDown
                                        name="typeId"
                                        value={data?.feedbackType?.find(c => c.code?.code === feedback?.typeId?.toString())}
                                        dataSource={data?.feedbackType as IContactPoint[]}
                                        label={tr("case_type")}
                                        itemDisplayFormat="displayText"
                                        valueKey="code"
                                        selectOptionsMode
                                        error={error.typeId}
                                        required
                                        nextElement="categoryId"
                                        onChange={onFeedbackChange}
                                    />
                                </div>
                                <div className="col-1-2">
                                    <DropDown
                                        name="categoryId"
                                        id="categoryId"
                                        value={data?.feedbackCategory?.find(c => c.code?.code === feedback?.categoryId?.toString())}
                                        dataSource={data?.feedbackCategory as IContactPoint[]}
                                        label={tr("subject")}
                                        itemDisplayFormat="displayText"
                                        valueKey="code"
                                        searchable={false}
                                        error={error.categoryId}
                                        required
                                        nextElement="description"
                                        onChange={onFeedbackChange}
                                    />
                                </div>
                            </div>
                            <div className="row desc">
                                <div className="col-1-1">
                                    <TextArea
                                        name="description"
                                        id="description"
                                        value={feedback?.description}
                                        label={tr("comment")}
                                        error={error.description}
                                        required
                                        onChange={(name, value) => removeValidation(name, value)}
                                        onBlur={onFeedbackChange}
                                    />
                                </div>
                            </div>
                            <div className="row attachment">
                                <div className="col-1-1">
                                    <div className="title">{tr("attachment")}</div>
                                    <FileInput
                                        name="attachments"
                                        value={feedback.attachments}
                                        multiple={true}
                                        onChange={onFileChange}
                                        onRemove={onFeedbackChange}
                                    />
                                </div>
                            </div>
                            <div className="row desc">
                                <div className="col-1-1">
                                    <PhoneSection
                                        type="phone"
                                        label={tr("mobile")}
                                        dataSource={feedback}
                                        autoComplete="on"
                                        dataMember="phoneNumber"
                                        error={error}
                                        freezeCallingCode={lang === IrLocale.lang ? IrLocale.phoneCode : ""}
                                        onChange={onMobileChange}
                                    />
                                </div>
                            </div>
                            <div className="row desc">
                                <div className="col-1-1">
                                    <TextInput
                                        name={nameof<IContactEmail>("emailAddress")}
                                        type="email"
                                        dataSource={feedback.email || Object.assign(feedback, { email: {} })}
                                        dataMember={nameof<IContactEmail>("emailAddress")}
                                        label={tr("email")}
                                        dir="ltr"
                                        error={error.emailAddress}
                                        onChange={removeValidation}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-1-1">
                                    {
                                        loadingStatus.modifyFeedback ?
                                            <div className="button-group">
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    text={tr("btn_edit")}
                                                    height="40px"
                                                    onClick={onModifyBtnClick}
                                                    loading={loadingStatus.submitBtnLoading}
                                                    disabled={loadingStatus.submitBtnLoading}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    color="black"
                                                    text={tr("btn_cancel")}
                                                    height="40px"
                                                    onClick={() => setLoadingStatus((previousState) => ({ ...previousState, addFeedbackBtn: true, modifyFeedback: false }))}
                                                    disabled={loadingStatus.submitBtnLoading}
                                                />
                                            </div>
                                            :
                                            <Button
                                                variant="contained"
                                                type="submit"
                                                color="primary"
                                                text={tr("feedback_confirm")}
                                                height="40px"
                                                loading={loadingStatus.submitBtnLoading}
                                                disabled={loadingStatus.submitBtnLoading}
                                            />
                                    }
                                </div>
                            </div>
                        </Form>
                    </CSSTransition>
                    {
                        participate.feedbacks?.length || loadingStatus.feedbacksSkeleton ? <div className="confirmed-comments">
                            <header className="feedback-confirmed-header">{tr("feedbacks_confirmed")}</header>
                            {
                                loadingStatus.feedbacksSkeleton ? <FeedbackItemsSkeleton />
                                    : participate.feedbacks?.map((feedback, index) =>
                                        <Fragment key={index}>
                                            <FeedbackConfirmedItems
                                                contactPointRes={data?.feedbackType as IContactPoint[]}
                                                comment={feedback}
                                                onModifyComment={onModifyFeedback}
                                                onRemoveComment={onRemoveFeedback}
                                            />
                                            <Divider mode="perforation" orientation="horizontal" />
                                        </Fragment>
                                    )
                            }
                        </div>
                            : ""
                    }
                    <Dialog
                        show={loadingStatus?.successfulMessageBoxDialog || false}
                        animate="fade"
                        onHiding={() => { setLoadingStatus((previousState) => ({ ...previousState, successfulMessageBoxDialog: false })); onUpdateParticipateFeedbacks(); }}
                    >
                        <DialogHeader className="flex flex-center">
                            <Label
                                icon="icon-success"
                                mode="inline"
                                text={tr(`feedback_${loadingStatus.modifyFeedback ? "modify" : "submit"}_successful`)}
                                size='large'
                                multi
                            />
                        </DialogHeader>
                        <main className="survey-submit-form-success-modal">
                            <section className="content-section">
                                <p>{tr("feedback_msg_successfully", feedback.referenceId)}</p>
                            </section>
                        </main>
                    </Dialog>
                    <MessageBox
                        show={loadingStatus.deleteFeedback || false}
                        btnGroup={MessageBoxBtnGroup.YesNo}
                        onAction={onRemoveBtnAction}
                    >
                        {tr("feedback_delete_msg")}
                    </MessageBox>
                </main>
            </Panel>
        </div >
    )
}